import { useState } from "react";
import { Pencil, FileSpreadsheet, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Money } from "@/components/common";
import { postDownload, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PostToLedgers from "@/components/PostToLedgers";

function Totals({ t }) {
  const items = [["Total Outstanding Advance", t.outstanding, "text-slate-900", "outstanding"], ["Total Deduction", t.deduction, "text-emerald-700", "deduction"], ["Total Remaining Balance", t.balance, "text-blue-800", "balance"]];
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid="statement-totals">
      {items.map(([l, v, c, id]) => (
        <div key={id} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
          <div className={`money text-2xl font-bold mt-1 ${c}`} data-testid={`statement-total-${id}`}><Money value={v} /></div>
        </div>
      ))}
    </div>
  );
}

export default function PayrollStatement({ statement, payload, onEdit }) {
  const [busy, setBusy] = useState("");
  const { can } = useAuth();
  const get = async (format) => {
    setBusy(format);
    try {
      await postDownload("/payroll-statement/generate", { ...payload, format });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const s = statement;
  return (
    <div className="space-y-5 fade-up" data-testid="statement-view">
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> Statement generated, review before downloading</div>
          <div className="text-xl font-heading font-bold text-slate-900 mt-1">Employee Advance Deduction Statement</div>
          <div className="text-sm text-slate-500" data-testid="statement-meta">Payroll period: <b>{s.payroll_period}</b> · Date: <b>{fmtDate(s.statement_date)}</b> · {s.rows.length} employees · Prepared by {s.prepared_by}</div>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          <Button variant="outline" onClick={onEdit} data-testid="statement-edit-btn"><Pencil className="h-4 w-4" /> Edit deductions</Button>
          {can("admin") && <PostToLedgers payload={payload} />}
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => get("xlsx")} disabled={!!busy} data-testid="statement-download-xlsx">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Download Excel (.xlsx)
          </Button>
          <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => get("pdf")} disabled={!!busy} data-testid="statement-download-pdf">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Download PDF (.pdf)
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="statement-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <tr><th className="px-4 py-2.5 text-left">Employee ID</th><th className="px-4 py-2.5 text-left">Employee Name</th><th className="px-4 py-2.5 text-right">Outstanding Advance</th><th className="px-4 py-2.5 text-right">Deduction</th><th className="px-4 py-2.5 text-right">Balance</th></tr>
          </thead>
          <tbody>
            {s.rows.map((r, i) => (
              <tr key={r.code} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`statement-row-${i}`}>
                <td className="px-4 py-2.5 money text-blue-800 font-semibold">{r.code}</td>
                <td className="px-4 py-2.5">{r.name}</td>
                <td className="px-4 py-2.5 text-right"><Money value={r.outstanding} /></td>
                <td className="px-4 py-2.5 text-right text-emerald-700" data-testid={`statement-deduction-${i}`}><Money value={r.deduction} /></td>
                <td className="px-4 py-2.5 text-right font-semibold" data-testid={`statement-balance-${i}`}><Money value={r.balance} /></td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-100 font-semibold border-t-2 border-blue-800">
            <tr>
              <td className="px-4 py-3" colSpan={2}>TOTAL ({s.rows.length} employees)</td>
              <td className="px-4 py-3 text-right"><Money value={s.totals.outstanding} /></td>
              <td className="px-4 py-3 text-right text-emerald-700"><Money value={s.totals.deduction} /></td>
              <td className="px-4 py-3 text-right text-blue-800"><Money value={s.totals.balance} /></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <Totals t={s.totals} />
    </div>
  );
}
